import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  MapPin, Clock, CalendarCheck, CircleNotch, ArrowLeft, UsersThree, WhatsappLogo,
  Copy, LinkSimple, WarningCircle, ClipboardText, ListChecks, Info, UsersFour, ArrowClockwise, Prohibit,
} from "@phosphor-icons/react";

const TEAM_LABEL = { dg: "District Governor", ds: "District Secretary", ag: "Assistant Governor", zg: "Zone Governor", zs: "Zone Secretary" };
const WARN_LABEL = {
  dg_missing_or_ambiguous: "District Governor not resolved",
  ag_unassigned_for_club: "No Assistant Governor assigned to this club",
  zg_unassigned_for_zone: "No Zone Governor for this club's zone",
  club_has_no_zone: "Club is not assigned to a zone",
};
const INV_STATUS = {
  issued: "border-primary/40 text-primary", accepted: "border-emerald-500/50 text-emerald-600",
  expired: "border-amber-500/50 text-amber-700", revoked: "border-black/15 text-muted-foreground",
  "not issued": "border-black/15 text-muted-foreground",
};

export default function VisitDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [visit, setVisit] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState({}); // in-memory one-time secure link/message per role

  const load = async () => {
    try {
      const v = await api.get(`/gov/visits/${id}`);
      setVisit(v.data);
      if (v.data.can_manage) {
        const inv = await api.get(`/gov/visits/${id}/invitations`);
        setInvitations(inv.data.invitations);
      }
    } catch (_) { setVisit(null); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [id]);

  const assignTeam = async () => {
    setBusy(true);
    try { const r = await api.post(`/gov/visits/${id}/team`); toast.success("Team resolved"); setVisit({ ...visit, team: r.data.team, team_warnings: r.data.team_warnings }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const issue = async (role) => {
    setBusy(true);
    try {
      const r = await api.post(`/gov/visits/${id}/invitations`, { intended_role: role });
      setIssued((s) => ({ ...s, [role]: r.data }));
      toast.success(`${role} invitation issued`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const revoke = async (invId) => {
    setBusy(true);
    try { await api.post(`/gov/invitations/${invId}/revoke`); toast.success("Revoked"); await load(); }
    catch (e) { toast.error("Failed"); }
    setBusy(false);
  };
  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground t-support"><CircleNotch size={18} className="animate-spin text-primary" weight="bold" /> loading visit…</div>;
  if (!visit) return <div data-testid="visit-forbidden" className="max-w-md border border-border bg-surface p-8 rounded-md"><WarningCircle size={26} weight="duotone" className="text-amber-600" /><p className="mt-3 t-body">This visit is not available in your scope.</p></div>;

  return (
    <div className="max-w-4xl" data-testid="visit-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 t-support text-muted-foreground hover:text-foreground mb-3"><ArrowLeft size={14} /> Back</button>
      <div className="flex items-center gap-2 eyebrow mb-2"><CalendarCheck size={14} weight="fill" className="text-primary" /> Governor's Official Visit</div>
      <h1 className="t-page-title">{visit.club_name}</h1>

      {/* Summary */}
      <div className="mt-5 grid sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl shadow-[0_1px_2px_rgba(84,86,90,0.06)] p-4"><div className="flex items-center gap-2 eyebrow"><CalendarCheck size={14} /> Date</div><p className="mt-1 t-body font-semibold">{visit.display_date}</p></div>
        <div className="bg-white rounded-2xl shadow-[0_1px_2px_rgba(84,86,90,0.06)] p-4"><div className="flex items-center gap-2 eyebrow"><Clock size={14} /> Time (IST)</div><p className="mt-1 t-body font-semibold">{visit.display_time}</p></div>
        <div className="bg-white rounded-2xl shadow-[0_1px_2px_rgba(84,86,90,0.06)] p-4"><div className="flex items-center gap-2 eyebrow"><MapPin size={14} /> Venue</div><p className="mt-1 t-support">{visit.venue_name || "—"}</p></div>
      </div>

      {/* Team */}
      <section className="mt-8">
        <div className="flex items-center justify-between">
          <h2 className="t-section-title flex items-center gap-2"><UsersThree size={20} weight="duotone" className="text-primary" /> District team</h2>
          {visit.can_manage && <button data-testid="assign-team-button" onClick={assignTeam} disabled={busy} className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 t-support font-semibold rounded-md hover:bg-secondary"><UsersFour size={14} weight="bold" /> Resolve team</button>}
        </div>
        <div className="mt-3 bg-white rounded-2xl shadow-[0_1px_2px_rgba(84,86,90,0.06)] p-4 grid sm:grid-cols-2 gap-3" data-testid="team-grid">
          {["dg", "ds", "ag", "zg", "zs"].map((k) => (
            <div key={k} className="flex items-center justify-between gap-2">
              <span className="eyebrow">{TEAM_LABEL[k]}</span>
              <span className="t-support font-medium">{visit.team?.[k]?.display_name || "—"}</span>
            </div>
          ))}
        </div>
        {visit.team_warnings?.length > 0 && (
          <div data-testid="team-warnings" className="mt-2 border border-amber-500/40 bg-amber-50 rounded-md p-3">
            {visit.team_warnings.map((w) => <p key={w} className="flex items-center gap-2 t-support text-amber-800"><WarningCircle size={15} weight="fill" /> {WARN_LABEL[w] || w}</p>)}
          </div>
        )}
      </section>

      {/* Invitation Centre (managers only) */}
      {visit.can_manage && (
        <section className="mt-8" data-testid="invitation-centre">
          <h2 className="t-section-title flex items-center gap-2"><ClipboardText size={20} weight="duotone" className="text-primary" /> Invitation Centre</h2>
          <div className="mt-3 grid sm:grid-cols-2 gap-4">
            {["President", "Secretary"].map((role) => {
              const inv = invitations.find((i) => i.intended_role === role);
              const status = inv ? inv.status : "not issued";
              const payload = issued[role];
              return (
                <div key={role} data-testid={`invite-card-${role}`} className="bg-white rounded-2xl shadow-[0_1px_2px_rgba(84,86,90,0.06)] p-4">
                  <div className="flex items-center justify-between">
                    <span className="t-body font-semibold">{role}</span>
                    <span className={`text-xs uppercase tracking-wide font-semibold px-2 py-0.5 border rounded ${INV_STATUS[status] || ""}`}>{status}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button data-testid={`issue-${role}`} onClick={() => issue(role)} disabled={busy}
                      className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground px-3 py-1.5 text-xs font-semibold rounded hover:bg-primary/90 active:scale-95 disabled:opacity-60">
                      {inv ? <><ArrowClockwise size={13} weight="bold" /> Regenerate</> : <>Issue</>}
                    </button>
                    {inv && inv.status === "issued" && (
                      <button data-testid={`revoke-${role}`} onClick={() => revoke(inv.invitation_id)} disabled={busy}
                        className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-xs font-semibold rounded text-destructive hover:bg-secondary"><Prohibit size={13} weight="bold" /> Revoke</button>
                    )}
                  </div>
                  {payload && (
                    <div className="mt-3 border-t border-border pt-3 space-y-2" data-testid={`payload-${role}`}>
                      <p className="t-support text-muted-foreground">Share this now — the secure link is shown once.</p>
                      <div className="flex flex-wrap gap-2">
                        <a data-testid={`wa-${role}`} href={payload.whatsapp_link || undefined}
                          onClick={(e) => { if (!payload.whatsapp_link) { e.preventDefault(); toast.error("No valid mobile — use Copy instead"); } }}
                          target="_blank" rel="noreferrer"
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded ${payload.whatsapp_link ? "bg-[#25D366] text-white hover:opacity-90" : "border border-border text-muted-foreground cursor-not-allowed"}`}>
                          <WhatsappLogo size={14} weight="fill" /> WhatsApp</a>
                        <button data-testid={`copy-msg-${role}`} onClick={() => copy(payload.message)} className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-xs font-semibold rounded hover:bg-secondary"><Copy size={13} weight="bold" /> Copy message</button>
                        <button data-testid={`copy-link-${role}`} onClick={() => copy(payload.secure_link)} className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-xs font-semibold rounded hover:bg-secondary"><LinkSimple size={13} weight="bold" /> Copy link</button>
                      </div>
                      {!payload.mobile_valid && <p className="t-support text-amber-700">No valid E.164 mobile — direct WhatsApp disabled; copy to share via email/SMS.</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Read-only preparation framework */}
      <section className="mt-8">
        <h2 className="t-section-title flex items-center gap-2"><ListChecks size={20} weight="duotone" className="text-primary" /> Preparation requirements</h2>
        <ol className="mt-3 bg-white rounded-2xl shadow-[0_1px_2px_rgba(84,86,90,0.06)] divide-y divide-border" data-testid="requirements-list">
          {visit.requirements.map((r, i) => (
            <li key={r.requirement_key} className="flex items-start gap-3 px-5 py-3">
              <span className="t-support font-mono text-muted-foreground w-6">{i + 1}</span>
              <span className="t-support flex-1">{r.label}</span>
              {r.focus_area && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 border border-primary/40 text-primary rounded self-center">focus</span>}
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-8 grid md:grid-cols-2 gap-6">
        <div>
          <h2 className="t-section-title">Agenda framework</h2>
          <div className="mt-3 bg-white rounded-2xl shadow-[0_1px_2px_rgba(84,86,90,0.06)] divide-y divide-border" data-testid="agenda-framework">
            {visit.agenda_framework.map((a) => (
              <div key={a.key} className="px-5 py-3">
                <p className="t-support font-semibold">{a.order}. {a.title}</p>
                <p className="t-support text-muted-foreground">{a.default_duration_min ? `${a.default_duration_min} min` : "Duration configurable"}{a.notes ? ` — ${a.notes}` : ""}</p>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h2 className="t-section-title flex items-center gap-2"><Info size={18} weight="duotone" className="text-primary" /> Official instructions</h2>
          <ul className="mt-3 bg-white rounded-2xl shadow-[0_1px_2px_rgba(84,86,90,0.06)] p-4 space-y-2" data-testid="operating-instructions">
            {visit.operating_instructions.map((i, idx) => <li key={idx} className="t-support flex gap-2"><span className="text-primary">•</span> {i}</li>)}
          </ul>
          <div className="mt-3 bg-white rounded-2xl shadow-[0_1px_2px_rgba(84,86,90,0.06)] p-4" data-testid="deadlines">
            <p className="eyebrow">Calculated deadlines</p>
            <p className="mt-1 t-support">Reconfirm arrangements by <b>{visit.deadlines.reconfirm_by}</b></p>
            <p className="t-support">Share minute-to-minute agenda with AG by <b>{visit.deadlines.agenda_to_ag_by}</b></p>
          </div>
        </div>
      </section>
    </div>
  );
}
